import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import { LoadingSpinner } from '@vaiu/ui';
import { Alert, AlertDescription } from '@vaiu/ui/alert';

interface StripeBankReturnHandlerProps {
  onComplete: () => void;
}

/**
 * Handles the return from Stripe redirect (e.g. after mandate acceptance).
 * When Stripe redirects, it appends setup_intent_client_secret and redirect_status to the URL.
 * This component completes the save flow that was interrupted by the redirect.
 */
const StripeBankReturnHandler: React.FC<StripeBankReturnHandlerProps> = ({ onComplete }) => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const clientSecret = searchParams.get('setup_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    if (!clientSecret || redirectStatus !== 'succeeded') {
      return;
    }

    let cancelled = false;

    const processReturn = async () => {
      if (!stripe) return;

      try {
        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret);
        if (cancelled) return;

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          throw new Error(setupIntent?.last_setup_error?.message ?? 'Bank setup was not completed');
        }

        const setupIntentId = typeof setupIntent.id === 'string' ? setupIntent.id : (setupIntent as any).id;
        const savedPaymentMethod = await platformApi.saveStripeBankAccount(setupIntentId);
        if (cancelled) return;

        // Clear Stripe params from URL
        const next = new URLSearchParams(searchParams);
        next.delete('setup_intent');
        next.delete('setup_intent_client_secret');
        next.delete('redirect_status');
        setSearchParams(next, { replace: true });

        toast({
          title: i18n.__('addPaymentMethod.success'),
          description: `Your ${i18n.__('vaiu.payment.bank_account')} has been connected.`,
        });

        onComplete();
        navigate('/payment-sources');
      } catch (err: any) {
        if (cancelled) return;
        console.error('Stripe bank return handling failed:', err);
        setError(err.message ?? 'Failed to complete bank connection');
      }
    };

    processReturn();
    return () => { cancelled = true; };
  }, [stripe, searchParams, setSearchParams, navigate, toast, onComplete]);

  const clientSecret = searchParams.get('setup_intent_client_secret');
  const redirectStatus = searchParams.get('redirect_status');

  if (!clientSecret || redirectStatus !== 'succeeded') {
    return null;
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertDescription>
          {error}
          <button
            type="button"
            onClick={() => {
              setSearchParams({}, { replace: true });
              window.location.reload();
            }}
            className="ml-2 underline"
          >
            Start over
          </button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <LoadingSpinner text="Completing bank connection..." />
      <p className="text-sm text-muted-foreground mt-4">
        Please wait while we save your bank account.
      </p>
    </div>
  );
};

export default StripeBankReturnHandler;
